import React, { useCallback, useMemo, useRef, useState } from 'react';
import Table from 'router/subrouters/Dashboard/components/Table';
import usePaginatedItems from '../../../hooks/usePaginatedItems';
import api from 'api';
import {
  InventoryPlacement,
  InventoryPlacementFormValues,
  InventoryPlacementSearchBy,
  inventoryPlacementSearchByOptions,
} from 'models/InventoryPlacement';
import useQueryParams from 'hooks/useQueryParams';
import { useParams } from 'react-router-dom';
import useLabels from './hooks/useLabels';
import { useTranslation } from 'react-i18next';
import notFound from '../../../../../../../../public/assets/images/notFound.png';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import Modal, { ModalRef } from 'components/Modal';
import InventoryPlacementForm from './components/InventoryPlacementForm';
import utils from 'utils';
import { toast } from 'react-toastify';

const InventoryPlacementPage = () => {
  const [selectedPlacement, setSelectedPlacement] =
    useState<InventoryPlacementFormValues>(null);

  const labels = useLabels();

  const { storeNo } = useParams();

  const { t } = useTranslation();

  const modalRef = useRef<ModalRef>();

  const {
    setQueryParam,
    params: { page, search, limit, searchBy },
  } = useQueryParams<{
    page: string;
    search: string;
    limit: string;
    searchBy: InventoryPlacementSearchBy;
  }>({ searchBy: 'skuNo' });

  const { items, refetch, totalItems, totalPages, isFetching } =
    usePaginatedItems({
      queryKey: 'inventory-placement',
      makeRequest: api.inventoryPlacement.getInventoryPlacement,
      searchParam: searchBy,
      params: {
        limit,
        page,
        [searchBy]: !!search && search.length >= 2 ? search : undefined,
        storeNo,
      },
    });

  const renderContentArray = useCallback((data: InventoryPlacement[]) => {
    return data?.map(
      ({ imageUrlEn, skuNo, descriptionEn, binNo, ...rest }) => ({
        id: `${skuNo}-${binNo}`,
        skuNo,
        imageUrl: imageUrlEn,
        name: descriptionEn,
        locationNumber: binNo,
        ...rest,
      }),
    );
  }, []);

  const handleSearchByChange = useCallback(
    (option: OptionValue<any>) => {
      setQueryParam('searchBy', option.value, true);
    },
    [setQueryParam],
  );

  useEffectSkipFirst(() => {
    page !== '1' && setQueryParam('page', '1', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, limit]);

  const searchByOptions = useMemo(
    () =>
      Object.values(inventoryPlacementSearchByOptions).map(
        ({ value, label }) => ({
          label: t(`${label}`),
          value,
        }),
      ),
    [t],
  );

  const onEditClick = useCallback((values: any) => {
    setSelectedPlacement({
      quantity: values.quantity,
      binNo: values.locationNumber,
      skuNo: values.skuNo,
    });
    modalRef.current.open();
  }, []);

  const onSubmit = useCallback(
    async (values: InventoryPlacementFormValues) => {
      const { quantity, skuNo, binNo } = values;
      try {
        await api.inventoryPlacement.addInventoryPlacement(
          skuNo,
          quantity,
          binNo,
          +storeNo,
        );
        refetch();
        setSelectedPlacement(null);
        modalRef.current.close();
      } catch (err) {
        toast.error(utils.handleResponseError(err));
      }
    },
    [refetch, storeNo],
  );
  return (
    <>
      <Table
        title={t('InventoryPlacement')}
        labels={labels}
        fetching={isFetching}
        fallbackNode={<img src={notFound} alt="notFound" />}
        contentArray={renderContentArray(items)}
        totalItems={totalItems}
        searchBy={searchBy}
        searchByOptions={searchByOptions}
        searchable
        onSearchByChange={handleSearchByChange}
        totalPages={totalPages}
        withActionButtons
        onEdit={onEditClick}
        onAddButtonClick={() => {
          modalRef.current.open();
        }}
        buttonText={t('add') || ''}
        elementProps={labels.map(({ key }) => key)}
        searchPlaceholder={t('inventoryPlacementSearchPlaceholder')}
      />
      <Modal
        focusableElements="div"
        modalName="manage-inventory-placement"
        className="manage-inventory-placement__modal"
        type="no-action"
        title={t('ManageInventoryPlacement')}
        ref={modalRef}
        onX={() => {
          setSelectedPlacement(null);
          modalRef.current.close();
        }}
      >
        <InventoryPlacementForm
          selectedInventoryPlacement={selectedPlacement}
          onSubmit={onSubmit}
          handleCancel={() => {
            setSelectedPlacement(null);
            modalRef.current.close();
          }}
        />
      </Modal>
    </>
  );
};

export default InventoryPlacementPage;
